import { ForwardedRef, forwardRef, memo, useEffect, useState } from "react";
import { View } from "react-native";
import log from "src/helpers/log";
import { paddingSmall } from "src/styles/spacing";
import { Text } from "src/swsh-native";
import Loading from "../Loading";
import ImageCropperContent from "./components/ImageCropperContent";
import fetchImageUri from "./helpers/fetchImageUri";
import { ImageCropperHandler, ImageCropperProps } from "./types";

const ImageCropper = (
	{
		src,
		containerStyle,
		width,
		height,
		onImageLoadError,
		onImageLoad,
		...props
	}: ImageCropperProps,
	ref: ForwardedRef<ImageCropperHandler>,
) => {
	const [fetchResult, setFetchResult] = useState<
		| {
				success: true;
				src: string;
				uri: string;
				width: number;
				height: number;
		  }
		| {
				success: false;
				src: null;
		  }
	>();
	useEffect(() => {
		(async () => {
			try {
				const resolvedSrc = await src;
				const result = await fetchImageUri(resolvedSrc);
				onImageLoad?.(result);
				if (result) {
					setFetchResult({
						success: true,
						src: resolvedSrc,
						uri: result.uri,
						width: result.width,
						height: result.height,
					});
				} else {
					setFetchResult({
						src: null,
						success: false,
					});
				}
			} catch (err) {
				log.error({ err }, "Failed to fetch image uri");
				onImageLoadError?.(err);
				setFetchResult({
					src: null,
					success: false,
				});
			}
		})();
	}, [src, onImageLoad, onImageLoadError]);

	return (
		<View
			style={[
				{
					flex: 1,
				},
				containerStyle,
				{
					width,
					height,
					maxWidth: width,
					maxHeight: height,
					alignItems: "center",
					justifyContent: "center",
				},
			]}
		>
			{!!(fetchResult && fetchResult.success) && (
				<ImageCropperContent
					// We want to refresh the image when the src changes
					key={fetchResult.src}
					uri={fetchResult.uri}
					{...props}
					width={width}
					height={height}
					imageWidth={fetchResult.width}
					imageHeight={fetchResult.height}
					parentRef={ref}
				/>
			)}
			{!fetchResult && <Loading />}
			{!!(fetchResult && !fetchResult.success) && (
				<View
					style={{
						alignItems: "center",
						rowGap: paddingSmall,
					}}
				>
					<Text color="Error" semibold>
						Failed to load image
					</Text>
					<Text color="TextLight">Please try again later</Text>
				</View>
			)}
		</View>
	);
};
export default memo(forwardRef(ImageCropper));
